import { useEffect, useRef, useState } from "react";

import { DashboardMapConstants, UserType } from "../../../constants";
import { getProjectPhaseLabel } from "../../../constants/project-phase";
import { Origin } from "../../../models/shared/origin";
import { logError } from "../../../service/error";
import { searchProjectLocations } from "../../../service/query";
import { Status } from "../../../service/Shared";
// Deep import: the utils barrel transitively pulls in the axios client, which this hook does not need.
import { getCountryNameByCode } from "../../../utils/common";
import { parseCoordinate } from "../../../utils/location";
import { getProjectDetailsByUuid } from "../../../utils/routes";
import { DashboardMapPin } from "./DashboardMapProps";

interface UseProjectMapPinsReturnData {
  projectPins: DashboardMapPin[];
  isLoadingProjectPins: boolean;
}

/* The map's overlay card and search results expect "<place>, <country>" — the form the
   Storybook pins use (e.g. "Cumbria, England"). Either half may be absent. */
const formatPinLocation = (addressCity: string | null, addressCountryCode: string | null): string | undefined =>
  [addressCity, getCountryNameByCode(addressCountryCode)].filter(Boolean).join(", ") || undefined;

/**
 * Loads every project location as a map pin for the dashboard map.
 *
 * The map fetches independently of the rest of the dashboard so a failure on either side
 * cannot blank the other, and search/filtering are handled client-side by DashboardMap over
 * these pins, so this runs once. A project the map cannot place is skipped rather than
 * dropped on the map at 0,0; a project carrying malformed coordinates is also reported
 * through logError, since that is bad data rather than a project simply having no location.
 *
 * On any failure the pins are left empty so the map still renders; pass `onError` to surface
 * that to the user.
 *
 * `userType` only picks the role prefix of each pin's project link, so it is read through a ref
 * rather than a dependency: it is fixed for a session, and re-fetching on it would break the
 * run-once contract above.
 */
export const useProjectMapPins = (userType: UserType, onError?: () => void): UseProjectMapPinsReturnData => {
  const [projectPins, setProjectPins] = useState<DashboardMapPin[]>([]);
  const [isLoadingProjectPins, setIsLoadingProjectPins] = useState(true);

  // Kept in a ref so an inline callback doesn't re-trigger the fetch on every render.
  const onErrorRef = useRef(onError);
  const userTypeRef = useRef(userType);
  useEffect(() => {
    onErrorRef.current = onError;
    userTypeRef.current = userType;
  });

  useEffect(() => {
    // Flipped by the cleanup below; a response that lands after unmount is ignored.
    let isCurrent = true;

    const fetchProjectPins = async (): Promise<void> => {
      try {
        const locationsRes = await searchProjectLocations({
          paging: {
            limit: DashboardMapConstants.MAXIMUM_PINS,
            beforeCursor: null,
            afterCursor: null,
          },
        });

        if (!isCurrent) return;

        if (locationsRes.status !== Status.Success || !locationsRes.data) {
          setProjectPins([]);
          onErrorRef.current?.();
          return;
        }

        // A project with no coordinates at all is simply unlocated; only malformed data is reported.
        const skippedProjects: string[] = [];

        setProjectPins(
          locationsRes.data.results.reduce<DashboardMapPin[]>((pins, project) => {
            if (project.locationCoordinates === null) return pins;

            const [latitudeValue, longitudeValue] = project.locationCoordinates.split(",");
            const latitude = parseCoordinate(latitudeValue);
            const longitude = parseCoordinate(longitudeValue);

            if (latitude === undefined || longitude === undefined) {
              const invalidValues: string[] = [];
              if (latitude === undefined) invalidValues.push("latitude");
              if (longitude === undefined) invalidValues.push("longitude");

              skippedProjects.push(
                `${project.uuid} ("${project.locationCoordinates}") — invalid ${invalidValues.join(" and ")}`
              );
              return pins;
            }

            pins.push({
              id: project.uuid,
              name: project.name,
              latitude,
              longitude,
              phase: getProjectPhaseLabel(project.phase),
              location: formatPinLocation(project.addressCity, project.addressCountryCode),
              link: getProjectDetailsByUuid(project.uuid, userTypeRef.current, { origin: Origin.DashboardPage }),
            });
            return pins;
          }, [])
        );

        /* Reported once per load rather than once per project: a systemic data problem would
           otherwise fire a POST /frontenderror for every result in the page. */
        if (skippedProjects.length > 0) {
          logError({
            error: `Dashboard map skipped ${skippedProjects.length} pin(s) with unusable locationCoordinates: ${skippedProjects.join(
              "; "
            )}`,
          });
        }
      } catch {
        if (isCurrent) {
          setProjectPins([]);
          onErrorRef.current?.();
        }
      } finally {
        if (isCurrent) setIsLoadingProjectPins(false);
      }
    };

    fetchProjectPins();

    return () => {
      isCurrent = false;
    };
  }, []);

  return { projectPins, isLoadingProjectPins };
};
